import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowDownWideNarrow,
    ArrowUpWideNarrow,
    ChevronLeft,
    ChevronRight,
    Expand,
    Globe2,
    Handshake,
    Heart,
    Images,
    Users,
    X,
} from "lucide-react";
import { Footer, GradientButton, Header } from "@/components";

// --- Gala Dinner -----------------------------------------------------------
import GalaDinner1 from "@/assets/images/social/Gala-Dinner/800718878_1050877297726506_2417041490045234606_n1.jpg";
import GalaDinner2 from "@/assets/images/social/Gala-Dinner/800718878_1050877297726506_2417041490045234606_n.jpg";
import GalaDinner3 from "@/assets/images/social/Gala-Dinner/1.jpeg";


import Eco2MMB from "@/assets/images/social/EC02-MMB-2025/eco2-mmb-1.jpeg";
import Eco2MMB1 from "@/assets/images/social/EC02-MMB-2025/eco2-mmb-2.jpeg";

// --- Teej Mela 2025 --------------------------------------------------------
import TeejMela1 from "@/assets/images/social/Teej-Mela-2025/DSC08550.jpg";
import TeejMela2 from "@/assets/images/social/Teej-Mela-2025/DSC09900.jpg";
import TeejMela3 from "@/assets/images/social/Teej-Mela-2025/DSC09967.jpg";
import TeejMela4 from "@/assets/images/social/Teej-Mela-2025/PSC09377.jpg";

import TeejMela2026 from "@/assets/images/social/Teej-Mela-2026/teej-mela-2026.jpg";
// --- Partner logos ---------------------------------------------------------
import EWNC from "@/assets/images/home/testimonial/EWNC.png";
import NepalSaihatya from "@/assets/images/home/testimonial/nepalsaihatya.png";
import MelbourneMaguvalu from "@/assets/images/home/testimonial/Maguvala-Bathukamma.webp";

/**
 * SAN micron — Social page
 *
 * Gallery items are ALBUMS (a folder of photos). `images[0]` is the cover
 * shown in the grid; clicking it opens a lightbox over that whole album.
 * `date` drives the newest/oldest sort, so keep it accurate per album.
 */

const PARTNER_LOGOS = [
    { src: NepalSaihatya, alt: "Nepal Saihatya" },
    { src: EWNC, alt: "EWNC" },
    { src: MelbourneMaguvalu, alt: "Melbourne Maguvalu" },
] as const;

type PhotoTone = "teal" | "violet" | "amber";
type SortOrder = "newest" | "oldest";

interface GalleryAlbum {
    id: string;
    images: string[];
    tone: PhotoTone;
    caption: string;
    category: string;
    /** ISO date — drives the sort. */
    date: string;
}

const GALLERY_ALBUMS: GalleryAlbum[] = [
    {
        id: "gala-dinner",
        images: [GalaDinner1, GalaDinner2, GalaDinner3],
        tone: "teal",
        caption: "Gala Dinner",
        category: "Gala Dinner",
        date: "2026-07-12",
    },
    {
        id: "EC02-MMB-2025",
        images: [Eco2MMB, Eco2MMB1],
        tone: "teal",
        caption: "EC02 MMB 2025",
        category: "EC02 MMB 2025",
        date: "2026-07-12",
    },
    {
        id: "teej-mela-2025",
        images: [
            TeejMela1,
            TeejMela2,
            TeejMela3,
            TeejMela4,
        ],
        tone: "amber",
        caption: "Teej Mela 2025",
        category: "Teej Mela",
        date: "2026-08-20",
    },
    {
        id: "teej-mela-2026",
        images: [
            TeejMela2026
        ],
        tone: "amber",
        caption: "Teej Mela 2026",
        category: "Teej Mela",
        date: "2026-09-16",
    },
];

const TONE_GRADIENTS: Record<PhotoTone, string> = {
    teal: "from-teal-950/40 via-transparent to-transparent",
    violet: "from-violet-950/40 via-transparent to-transparent",
    amber: "from-amber-950/30 via-transparent to-transparent",
};

interface PhotoSlotProps {
    src: string;
    tone?: PhotoTone;
    caption?: string;
    badge?: string;
    className?: string;
    onClick?: () => void;
    /** e.g. "top" to avoid cropping heads in tall group photos. */
    objectPosition?: string;
}

function PhotoSlot({
    src,
    tone = "teal",
    caption,
    badge,
    className = "",
    onClick,
    objectPosition = "center",
}: PhotoSlotProps) {
    return (
        <div
            onClick={onClick}
            className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-[#101013] ${
                onClick ? "cursor-pointer" : ""
            } ${className}`}
        >
            <img
                src={src}
                alt={caption ?? "SAN micron community photo"}
                className="absolute inset-0 h-full w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-105"
                style={{ objectPosition }}
                loading="lazy"
            />
            <div className={`absolute inset-0 bg-gradient-to-br ${TONE_GRADIENTS[tone]}`} />

            {onClick && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-300 group-hover:bg-black/30 group-hover:opacity-100">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm">
                        <Expand size={18} className="text-white" />
                    </span>
                </div>
            )}

            {badge && (
                <div className="absolute right-3 top-3 flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 text-xs font-medium text-white/85 backdrop-blur-sm">
                    <Images size={12} />
                    {badge}
                </div>
            )}

            {caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-4 py-3">
                    <p className="text-sm text-white/85 lg:text-base">{caption}</p>
                </div>
            )}
        </div>
    );
}

export default function Social() {
    const navigate = useNavigate();

    const goToContact = useCallback(() => navigate("/contact-us"), [navigate]);

    const [sortOrder, setSortOrder] = useState<SortOrder>("newest");
    const [albumIndex, setAlbumIndex] = useState<number | null>(null);
    const [imageIndex, setImageIndex] = useState(0);

    const sortedAlbums = useMemo(
        () =>
            [...GALLERY_ALBUMS].sort((a, b) => {
                const diff = new Date(a.date).getTime() - new Date(b.date).getTime();
                return sortOrder === "newest" ? -diff : diff;
            }),
        [sortOrder]
    );

    const activeAlbum = albumIndex !== null ? sortedAlbums[albumIndex] : null;
    const activeImage = activeAlbum?.images[imageIndex];

    const openAlbum = useCallback((index: number) => {
        setAlbumIndex(index);
        setImageIndex(0);
    }, []);

    const closeLightbox = useCallback(() => {
        setAlbumIndex(null);
        setImageIndex(0);
    }, []);

    const showPrevImage = useCallback(() => {
        if (!activeAlbum) return;
        setImageIndex((prev) => (prev - 1 + activeAlbum.images.length) % activeAlbum.images.length);
    }, [activeAlbum]);

    const showNextImage = useCallback(() => {
        if (!activeAlbum) return;
        setImageIndex((prev) => (prev + 1) % activeAlbum.images.length);
    }, [activeAlbum]);

    // Escape closes the lightbox, arrows step through the open album.
    useEffect(() => {
        if (albumIndex === null) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") closeLightbox();
            if (e.key === "ArrowLeft") showPrevImage();
            if (e.key === "ArrowRight") showNextImage();
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [albumIndex, closeLightbox, showPrevImage, showNextImage]);

    return (
        <>
            <Header />

            <div className="min-h-screen bg-[#08080B] text-white">
                <div className="mx-auto max-w-6xl px-6 py-12 sm:py-16">
                    {/* Hero — image left, copy right */}
                    <section className="grid grid-cols-1 items-center gap-12 pb-20 lg:grid-cols-2 lg:gap-16">
                        <div className="relative order-2 lg:order-1">
                            <div className="absolute -inset-6 -z-10 rounded-[2.5rem] bg-gradient-to-br from-cyan-500/20 via-violet-500/10 to-transparent blur-3xl" />
                            <div className="h-[300px] overflow-hidden rounded-3xl border border-white/10 bg-[#101013] sm:h-[380px] lg:h-[500px]">
                                <img
                                    src={GalaDinner2}
                                    alt="SAN micron community at the Gala Dinner"
                                    className="h-full w-full object-cover object-top"
                                />
                            </div>
                        </div>

                        <div className="order-1 lg:order-2">
                            <p className="text-sm font-medium text-sky-400">Social Engagement</p>

                            <h1 className="mt-3 text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
                                SAN micron is strengthening communities
                                <br />
                                <span className="bg-gradient-to-r from-cyan-400 to-violet-500 bg-clip-text text-transparent">
                                    through technology
                                </span>
                            </h1>

                            <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/60">
                                We believe technology can create stronger, more connected communities.
                                SAN micron contributes its time, skills, and resources through partnerships,
                                mentorship, digital support, and initiatives that make a meaningful difference.
                            </p>

                            <div className="mt-8 flex flex-wrap gap-4">
                                <a
                                    href="#gallery"
                                    className="rounded-lg bg-gradient-to-r from-violet-600 to-blue-600 px-6 py-3 font-semibold text-white transition-opacity hover:opacity-90"
                                >
                                    See our impact
                                </a>
                                <button
                                    type="button"
                                    onClick={goToContact}
                                    className="rounded-lg border border-white/20 px-6 py-3 font-semibold text-white transition-colors hover:bg-white/5"
                                >
                                    Partner with us
                                </button>
                            </div>
                        </div>
                    </section>

                    {/* Highlights — bento grid */}
                    <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <div className="flex flex-col justify-between rounded-3xl border border-white/10 bg-[#111113] p-6 md:min-h-[400px] lg:p-8">
                            <h3 className="text-xl font-medium leading-snug">
                                Celebrating our community at the Gala Dinner
                            </h3>
                            <PhotoSlot
                                src={GalaDinner1}
                                tone="teal"
                                caption="Gala Dinner"
                                objectPosition="top"
                                className="mt-6 min-h-0 flex-1"
                            />
                        </div>

                        <div className="flex flex-col justify-between rounded-3xl border border-emerald-500/10 bg-gradient-to-br from-[#0d1f1a] to-[#0a0a0d] p-6 md:min-h-[400px] lg:p-8">
                            <h3 className="text-xl font-medium leading-snug">
                                Pro-bono development and hosting support for local nonprofits
                            </h3>
                            <div className="mt-10 flex items-center gap-3">
                                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-400">
                                    <Heart size={16} />
                                </span>
                                <span className="text-emerald-400">giving back</span>
                            </div>
                        </div>

                        <div className="flex flex-col overflow-hidden rounded-3xl border border-violet-500/10 bg-gradient-to-br from-[#15121f] to-[#0a0a0d] md:min-h-[400px]">
                            <div className="flex shrink-0 flex-col items-center gap-4 px-6 pb-6 pt-8 text-center lg:px-8">
                                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-violet-400/30 text-violet-300">
                                    <Globe2 size={20} strokeWidth={1.7} />
                                </span>
                                <h3 className="text-xl font-medium">
                                    Celebrating culture together at Teej Mela 2025
                                </h3>
                            </div>
                            <PhotoSlot
                                src={TeejMela1}
                                tone="violet"
                                caption="Teej Mela 2025"
                                className="mx-4 mb-4 min-h-0 flex-1"
                            />
                        </div>

                        {/* Event photo (replaces the former internships tile) */}
                        <PhotoSlot
                            src={TeejMela4}
                            tone="amber"
                            caption="Teej Mela 2025"
                            className="min-h-[280px] md:h-full"
                        />

                        <div className="flex flex-col gap-6 rounded-3xl border border-white/10 bg-[#111113] p-6 md:col-span-2 md:flex-row md:items-center lg:p-8">
                            <div className="md:w-2/5">
                                <h3 className="text-xl font-medium leading-snug">
                                    Standing alongside EWNC in service of the community
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-white/55">
                                    Our partnership with EWNC brought together volunteers and
                                    supporters to celebrate the year's work and recognise the
                                    people who made it possible.
                                </p>
                            </div>
                            <PhotoSlot
                                src={Eco2MMB1}
                                tone="amber"
                                caption="EWNC"
                                objectPosition="top"
                                className="h-56 md:h-64 md:w-3/5"
                            />
                        </div>
                    </section>

                    {/* Partnerships strip */}
                    <section className="mt-14">
                        <div className="inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.03] py-1.5 pl-1.5 pr-4">
                            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-cyan-500/15 text-cyan-400">
                                <Handshake size={14} />
                            </span>
                            <span className="text-xs font-bold uppercase tracking-wider text-white/80">
                                Organizations we have supported
                            </span>
                        </div>

                        <div className="mt-8 flex flex-wrap items-center justify-center gap-6 sm:gap-10">
                            {PARTNER_LOGOS.map((logo) => (
                                <div
                                    key={logo.alt}
                                    className="flex h-24 w-40 items-center justify-center rounded-2xl bg-white px-4 py-4 shadow-[0_8px_24px_-8px_rgba(0,0,0,0.5)] transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_16px_32px_-8px_rgba(34,211,238,0.35)] sm:h-32 sm:w-64"
                                >
                                    <img
                                        src={logo.src}
                                        alt={logo.alt}
                                        className="max-h-full max-w-full object-contain"
                                    />
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Gallery — sortable albums with a lightbox */}
                    <section id="gallery" className="mt-16">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div className="inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.03] py-1.5 pl-1.5 pr-4">
                                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-violet-500/15 text-violet-400">
                                    <Users size={14} />
                                </span>
                                <span className="text-xs font-bold uppercase tracking-wider text-white/80">
                                    Events we participate in
                                </span>
                            </div>

                            <button
                                type="button"
                                onClick={() =>
                                    setSortOrder((prev) => (prev === "newest" ? "oldest" : "newest"))
                                }
                                className="flex w-fit items-center gap-2.5 rounded-full border border-white/10 bg-[#111113] px-5 py-3 text-sm font-semibold text-white/80 transition-colors hover:border-white/20 hover:text-white"
                            >
                                {sortOrder === "newest" ? (
                                    <ArrowDownWideNarrow size={18} />
                                ) : (
                                    <ArrowUpWideNarrow size={18} />
                                )}
                                {sortOrder === "newest" ? "Newest first" : "Oldest first"}
                            </button>
                        </div>

                        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {sortedAlbums.map((album, index) => (
                                <PhotoSlot
                                    key={album.id}
                                    src={album.images[0]}
                                    tone={album.tone}
                                    caption={album.caption}
                                    badge={
                                        album.images.length > 1 ? `${album.images.length} photos` : undefined
                                    }
                                    className="h-72"
                                    onClick={() => openAlbum(index)}
                                />
                            ))}
                        </div>
                    </section>

                    {/* CTA */}
                    <section className="mt-16 flex flex-col items-start justify-between gap-6 rounded-3xl border border-white/10 bg-[#111113] p-6 sm:flex-row sm:items-center lg:p-8">
                        <div>
                            <h3 className="text-xl font-medium">Know a cause we should hear about?</h3>
                            <p className="mt-2 text-sm text-white/55">
                                We're always open to new community partnerships and requests.
                            </p>
                        </div>
                        <GradientButton onClick={goToContact}>Get in touch</GradientButton>
                    </section>
                </div>
            </div>

            <Footer />

            {/* Lightbox — one album, steps through its photos */}
            {activeAlbum && activeImage && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm"
                    onClick={closeLightbox}
                >
                    <button
                        type="button"
                        onClick={closeLightbox}
                        className="absolute right-5 top-5 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition-colors hover:bg-white/10"
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>

                    {activeAlbum.images.length > 1 && (
                        <>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    showPrevImage();
                                }}
                                className="absolute left-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition-colors hover:bg-white/10 sm:left-6"
                                aria-label="Previous photo"
                            >
                                <ChevronLeft size={20} />
                            </button>

                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    showNextImage();
                                }}
                                className="absolute right-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition-colors hover:bg-white/10 sm:right-6"
                                aria-label="Next photo"
                            >
                                <ChevronRight size={20} />
                            </button>
                        </>
                    )}

                    <div
                        className="mx-6 flex max-h-[85vh] max-w-4xl flex-col items-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={activeImage}
                            alt={`${activeAlbum.caption} — photo ${imageIndex + 1}`}
                            className="max-h-[70vh] w-auto rounded-2xl object-contain"
                        />

                        <div className="mt-4 flex items-center gap-3 text-center">
                            <p className="text-base text-white/90">{activeAlbum.caption}</p>
                            <span className="rounded-full border border-white/15 px-3 py-1 text-xs text-white/50">
                                {activeAlbum.category}
                            </span>
                            {activeAlbum.images.length > 1 && (
                                <span className="text-xs text-white/40">
                                    {imageIndex + 1} / {activeAlbum.images.length}
                                </span>
                            )}
                        </div>

                        {activeAlbum.images.length > 1 && (
                            <div className="mt-4 flex max-w-full gap-2 overflow-x-auto pb-1">
                                {activeAlbum.images.map((img, i) => (
                                    <button
                                        key={img}
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setImageIndex(i);
                                        }}
                                        className={`h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg border-2 transition-colors ${
                                            i === imageIndex
                                                ? "border-cyan-400"
                                                : "border-transparent opacity-60 hover:opacity-100"
                                        }`}
                                    >
                                        <img src={img} alt="" className="h-full w-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}